<script setup lang="ts">
/**
 * When the group's reminders go out, and to whom (SPEC §8.18 — MT-156).
 *
 * One card per reminder, always four, in the order they arrive before a meal.
 * A reminder that is off still has its card: "off" is a setting somebody
 * chose, and a list that hid it would leave nobody able to find it again.
 *
 * Each card saves on its own. Four reminders are four decisions, and one Save
 * button for all of them turns a change to one into a question about the rest.
 *
 * Times are the group's local times. The note under the heading says so,
 * because an organizer travelling is exactly the person who would otherwise
 * set 6 PM in the wrong zone.
 */
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { endpoints, type ReminderRuleResponse } from '@/api'
import { ApiError } from '@/api/client-exports'
import { useGroupContextStore } from '@/stores/groupContext'
import { asCount } from '@/utils/numbers'
import { audienceLabel, kindHint, kindLabel, RuleAudiences, timingLine } from '@/utils/reminders'

interface Draft {
  enabled: boolean
  daysBefore: number
  localTime: string
  audience: string
}

const route = useRoute()
const groupContext = useGroupContextStore()
const groupId = route.params.groupId as string

const rules = ref<ReminderRuleResponse[]>([])
const drafts = reactive<Record<string, Draft>>({})
const saving = ref<string | null>(null)
const saved = ref<string | null>(null)
const errors = reactive<Record<string, string | null>>({})
const loading = ref(true)
const loadError = ref<string | null>(null)

const timeZone = computed(
  () => groupContext.groups.find((g) => g.id === groupId)?.timeZoneId ?? null,
)

function draftFrom(rule: ReminderRuleResponse): Draft {
  return {
    enabled: rule.enabled,
    daysBefore: asCount(rule.daysBefore),
    localTime: rule.localTime,
    audience: rule.audience,
  }
}

function changed(rule: ReminderRuleResponse): boolean {
  const draft = drafts[rule.kind]
  if (!draft) return false
  const was = draftFrom(rule)
  return (
    draft.enabled !== was.enabled ||
    Number(draft.daysBefore) !== was.daysBefore ||
    draft.localTime !== was.localTime ||
    draft.audience !== was.audience
  )
}

async function load(): Promise<void> {
  loading.value = true
  loadError.value = null

  try {
    await groupContext.ensureLoaded()
    rules.value = await endpoints.reminderRules(groupId)
    for (const rule of rules.value) drafts[rule.kind] = draftFrom(rule)
  } catch {
    loadError.value = 'The reminders could not be loaded just now. Try again in a moment.'
  } finally {
    loading.value = false
  }
}

async function save(rule: ReminderRuleResponse): Promise<void> {
  const draft = drafts[rule.kind]
  if (!draft) return

  saving.value = rule.kind
  saved.value = null
  errors[rule.kind] = null

  try {
    const updated = await endpoints.updateReminderRule(groupId, rule.kind, {
      enabled: draft.enabled,
      daysBefore: Number(draft.daysBefore),
      localTime: draft.localTime,
      audience: draft.audience,
    })

    rules.value = rules.value.map((r) => (r.kind === updated.kind ? updated : r))
    drafts[updated.kind] = draftFrom(updated)
    saved.value = updated.kind
  } catch (caught) {
    errors[rule.kind] =
      (caught instanceof ApiError &&
        (Object.values(caught.problem?.errors ?? {})[0]?.[0] ?? caught.problem?.title)) ||
      'That did not save. Try again in a moment.'
  } finally {
    saving.value = null
  }
}

onMounted(load)
</script>

<template>
  <v-container class="py-10" style="max-width: 40rem">
    <h1 class="text-h5 mb-1">Reminders</h1>
    <p class="reminders__intro">
      Sent by email to each household's confirmed addresses.
      <template v-if="timeZone">Times are {{ timeZone.replace('_', ' ') }} time.</template>
    </p>

    <div v-if="loading" class="py-8 text-body-2" role="status">Loading…</div>

    <v-alert v-else-if="loadError" type="error" variant="tonal">{{ loadError }}</v-alert>

    <template v-else>
      <v-card
        v-for="rule in rules"
        :key="rule.kind"
        variant="outlined"
        class="mb-4"
        :data-testid="`rule-${rule.kind}`"
      >
        <v-card-item>
          <v-card-title tag="h2" class="text-subtitle-1 font-weight-bold">
            {{ kindLabel(rule.kind) }}
          </v-card-title>
          <v-card-subtitle class="reminders__hint">{{ kindHint(rule.kind) }}</v-card-subtitle>

          <template #append>
            <v-switch
              v-model="drafts[rule.kind]!.enabled"
              color="primary"
              hide-details
              inset
              :aria-label="`${kindLabel(rule.kind)} on`"
              data-testid="rule-enabled"
            />
          </template>
        </v-card-item>

        <v-card-text>
          <p class="reminders__summary" data-testid="rule-summary">
            <template v-if="rule.enabled">
              {{ timingLine(Number(rule.daysBefore), rule.localTime) }} ·
              {{ audienceLabel(rule.audience) }}
            </template>
            <template v-else>Off</template>
          </p>

          <div class="reminders__fields">
            <v-text-field
              v-model.number="drafts[rule.kind]!.daysBefore"
              type="number"
              min="0"
              max="30"
              label="Days before"
              density="comfortable"
              hide-details
              :name="`${rule.kind}-days`"
            />
            <v-text-field
              v-model="drafts[rule.kind]!.localTime"
              type="time"
              label="At"
              density="comfortable"
              hide-details
              :name="`${rule.kind}-time`"
            />
            <v-select
              v-model="drafts[rule.kind]!.audience"
              :items="RuleAudiences"
              label="To"
              density="comfortable"
              hide-details
              class="reminders__audience"
            />
          </div>

          <v-alert
            v-if="errors[rule.kind]"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ errors[rule.kind] }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <span v-if="saved === rule.kind" class="reminders__saved" role="status">Saved</span>
          <v-spacer />
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!changed(rule) || saving !== null"
            :loading="saving === rule.kind"
            data-testid="rule-save"
            @click="save(rule)"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-container>
</template>

<style scoped>
.reminders__intro {
  margin: 0 0 24px;
  color: rgb(var(--v-theme-on-surface-variant));
}

.reminders__hint {
  white-space: normal;
}

.reminders__summary {
  margin: 0 0 16px;
  font-weight: 500;
}

.reminders__fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.reminders__audience {
  grid-column: 1 / -1;
}

.reminders__saved {
  padding-left: 8px;
  color: rgb(var(--v-theme-success));
  font-weight: 500;
}
</style>
